import axios from "axios"
import { setAuthToken, tokenConfig } from "../helpers/setAuthToken";
import { setExpiredSession } from "../helpers/expiredSession"
import { setLogged, setLoginFail, setWrongPasswordValidation, setAccountActive, setResetEmailSent, setResetPasswordSuccess, setResetPasswordError } from "../features/login/loginSlice";

// Tipo para representar un token de Azure AD
export interface AzureAdTokenInfo {
  idToken: string;
  accessToken: string;
  email: string;
  serverUrl: string;
}

export interface ILoginResponse {
    access: string;
    refresh: string;
    server_url: string;
};

export interface ILoginState {
  logged: boolean;
  loginFail: boolean;
  loginErrorMessage: string;
  wrongNewPassword: boolean;
  passwordValidationMessages: string[];
  accountActive: boolean;
  resetEmailSent: boolean;
  resetPasswordSuccess: boolean;
  resetPasswordError: string | null;
};

export const sendResetPasswordEmail = (email: string, serverUrl: string) => async (dispatch: any) => {
  try {
    const form_data = new FormData();
    form_data.append('email', email);

    await axios.post(
      `${serverUrl}/api/send_reset_password_email/`,
      form_data,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    dispatch(setResetEmailSent());
    return true;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.log('error message: ', error);
      return false;
    } else {
      console.log('unexpected error: ', error);
      return false;
    }
  }
};

export const resetPassword = ({uid, token, newPassword, serverUrl}: {uid: string, token: string, newPassword: string, serverUrl: string}) => async (dispatch: any) => {
  try {
    const form_data = new FormData();
    form_data.append('uid', uid);
    form_data.append('token', token);
    form_data.append('new_password', newPassword);

    await axios.post(
      `${serverUrl}/api/reset_password/`,
      form_data,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    dispatch(setResetPasswordSuccess());
    return true;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.log('error message: ', error);
      dispatch(setResetPasswordError(error.response?.data?.message || 'Error al restablecer la contraseña'));
      return false;
    } else {
      console.log('unexpected error: ', error);
      dispatch(setResetPasswordError('Error inesperado al restablecer la contraseña'));
      return false;
    }
  }
};

export const activateAccount = ({uuid, token, oldPassword, newPassword, serverUrl}: {uuid:string, token:string, oldPassword:string, newPassword:string, serverUrl:string}) => async (dispatch:any) => {
  try {
      // Form Data
      const form_data = new FormData();
      form_data.append('uuid', uuid);
      form_data.append('token', token);
      form_data.append('old_password', oldPassword);
      form_data.append('new_password', newPassword);

      console.log("post activate account")
      await axios.post<ILoginResponse>( 
          `${serverUrl}/api/activate_account/`,
          form_data,
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          },
      );
      
      console.log("Activada con éxito")

      dispatch(setAccountActive())

    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log('error message: ', error);
        if (error.response?.data.code === 2) {
          dispatch(setWrongPasswordValidation(error.response?.data.response))
        } else if (error.response?.data.code === 1) {
          alert("La contraseña actual no coincide")
        } else {
          alert(error.response?.data.response)
        }
        return error.message;
      } else {
        console.log('unexpected error: ', error);
        return 'An unexpected error occurred';
      }
    }
}

export const getLoginInfo = (props:any) => async (dispatch:any) => {
    try {
        const { data } = await axios.post<ILoginResponse>( 
            `${props.serverUrl}/api/token/`,
          { username: props.username, password: props.password },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          },
        );

        localStorage.setItem("access", data['access']); 
        localStorage.setItem("refresh", data['refresh']);
        localStorage.setItem("server_url", props.serverUrl);
        localStorage.setItem("logged", "true");
        // Indicar que no se usó Azure AD para este login
        localStorage.setItem("used_adf_auth", "false");
        setAuthToken(data['access'])

        dispatch(setLogged())

        window.location.href = '/'
    } catch (error) {
        if (axios.isAxiosError(error)) {
          localStorage.setItem("logged", "false");
          const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Error de conexión';
          dispatch(setLoginFail(errorMessage))
          console.log('error message: ', errorMessage);
          return errorMessage;
        } else {
          console.log('unexpected error: ', error);
          const errorMessage = 'Error inesperado';
          dispatch(setLoginFail(errorMessage))
          return errorMessage;
        }
    }
}

// Autenticación con Azure AD
export const getAzureAdLoginInfo = (props: AzureAdTokenInfo) => async (dispatch:any) => {
    try {
        // Asegurarse de que serverURL no sea null
        const serverURL = props.serverUrl || localStorage.getItem("server_url") || "";
        if (!serverURL) {
            throw new Error("No se ha especificado una URL de servidor");
        }
        
        // DEBUG: Mostrar datos que se envían al backend
        console.log('=== Enviando datos al backend ===');
        console.log('Server URL:', serverURL);
        console.log('ID Token (first 100 chars):', props.idToken ? props.idToken.substring(0, 100) + '...' : 'NULL');
        console.log('Email:', props.email);
        console.log('Payload completo:', { id_token: props.idToken, email: props.email });
        console.log('================================');
        
        const { data } = await axios.post<ILoginResponse>(
            `${serverURL}/api/azure_ad_token/`,
            { id_token: props.idToken, access_token: props.accessToken, email: props.email },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            },
        );
        
        localStorage.setItem("access", data['access']); 
        localStorage.setItem("refresh", data['refresh']);
        localStorage.setItem("server_url", serverURL);
        localStorage.setItem("logged", "true");
        // Indicar que se usó Azure AD para este login
        localStorage.setItem("used_adf_auth", "true");
        setAuthToken(data['access'])
        
        // Actualizar la interfaz ILoginResponse para incluir user
        if (data && (data as any).user) {
            const userData = (data as any).user;
            localStorage.setItem("email", userData.email || "");
            localStorage.setItem("first_name", userData.first_name || "");
            localStorage.setItem("last_name", userData.last_name || "");
            localStorage.setItem("username", userData.username || "");
        }
        
        dispatch(setLogged())
        
        // Redirigir al inicio
        window.location.href = '/'
    } catch (error) {
        if (axios.isAxiosError(error)) {
            localStorage.setItem("logged", "false");
            const errorMessage = error.response?.data?.response || error.response?.data?.detail || error.response?.data?.message || error.message || 'Error de conexión con Azure AD';
            dispatch(setLoginFail(errorMessage))
            console.error('Error en la autenticación con Azure AD:', errorMessage);
            return errorMessage;
        } else {
            console.error('Error inesperado en la autenticación con Azure AD:', error);
            const errorMessage = 'Error inesperado en Azure AD';
            dispatch(setLoginFail(errorMessage))
            return errorMessage;
        }
    }
}

export const tokenUpdate = () => async (dispatch:any) => {
  try {
      const serverUrl = localStorage.getItem("server_url")
      const usedAdfAuth = localStorage.getItem("used_adf_auth") === "true";

      // Para autenticación ADF, intentar renovar el token silenciosamente
      if (usedAdfAuth) {
        // Si se usó ADF, intentar renovar tokens usando MSAL
        // Esto se manejará en el AzureAuthProvider cuando sea necesario
        console.log("Token update para ADF se maneja automáticamente por MSAL");
        return;
      }

      // Para autenticación tradicional, usar el endpoint de refresh
      const { data } = await axios.post<ILoginResponse>( 
          `${serverUrl}/api/token/refresh/`,
        { refresh: localStorage.getItem("refresh") },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      );

      localStorage.setItem("access", data['access']); 
      localStorage.setItem("refresh", data['refresh']);
      setAuthToken(data['access'])

    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log('error message: ', error.message);
        setExpiredSession();
        return error.message;
      } else {
        console.log('unexpected error: ', error);
        return 'An unexpected error occurred';
      }
    }
}

export const changePassword = ({oldPassword, newPassword}: {oldPassword:string, newPassword:string}) => async (dispatch:any) => {
  try {
      const serverUrl = localStorage.getItem("server_url")

      // Form Data
      const form_data = new FormData();
      form_data.append('old_password', oldPassword);
      form_data.append('new_password', newPassword);

      await axios.post<ILoginResponse>( 
        `${serverUrl}/api/change_password/`,
        form_data,
        tokenConfig(),
      );

      alert("Contraseña cambiada con éxito. Se le redirigirá hacia la pantalla de ingreso para que entre con su nueva contraseña.")
      window.location.href = '/'

    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log('error message: ', error);
        if (error.response?.data.code === 2) {
          dispatch(setWrongPasswordValidation(error.response?.data.response))
        } else if (error.response?.data.code === 1) {
          alert("La contraseña actual no coincide")
        }
        return error.message;
      } else {
        console.log('unexpected error: ', error);
        return 'An unexpected error occurred';
      }
    }
}

export const deviceFCMToken = (token:any) => async (dispatch:any) => {
  const serverUrl = localStorage.getItem("server_url")

  try {
      // Form Data
      const form_data = new FormData();
      form_data.append('token', token);

      await axios.post<any>( 
          `${serverUrl}/api/device_fcm_token/`,
          form_data,
          tokenConfig()
      );

  } catch (error) {
      if (axios.isAxiosError(error)) {
          console.log('error message: ', error.message);
          return error.message;
      } else {
          console.log('unexpected error: ', error);
          return 'An unexpected error occurred';
      }
  }
}
